import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BookOpen, Upload, ExternalLink, FileText, Search, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import HighlightableText from "@/components/HighlightableText";
import DocumentVersions from "@/components/DocumentVersions";
import { SourceTypeBadge, ApplicabilityNote, UNAVAILABLE } from "@/components/SourceDetails";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { BadgeCheck, MapPin } from "lucide-react";
import { CoverageBadge } from "@/components/JurisdictionSelector";
import { downloadFile } from "@/lib/upload";
import DocumentAudience, { AudienceSelect } from "@/components/DocumentAudience";

function MetaSelects({ form, setForm, options, prefix }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <div>
        <Label>Source type</Label>
        <Select value={form.source_type || ""} onValueChange={(v) => setForm({ ...form, source_type: v })}>
          <SelectTrigger data-testid={`${prefix}-source-type`} className="mt-1"><SelectValue placeholder="Auto-detect" /></SelectTrigger>
          <SelectContent>{options.source_types.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <Label>Jurisdiction</Label>
        <Select value={form.jurisdiction || ""} onValueChange={(v) => setForm({ ...form, jurisdiction: v })}>
          <SelectTrigger data-testid={`${prefix}-jurisdiction`} className="mt-1"><SelectValue placeholder="Auto-detect" /></SelectTrigger>
          <SelectContent>{options.jurisdictions.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <Label>Issuing agency / organization</Label>
        <Input data-testid={`${prefix}-agency`} placeholder="e.g. U.S. Department of Labor" value={form.agency || ""} onChange={(e) => setForm({ ...form, agency: e.target.value })} className="mt-1" />
      </div>
      <div>
        <Label>Publication / revision date</Label>
        <Input data-testid={`${prefix}-published`} type="date" value={form.published || ""} onChange={(e) => setForm({ ...form, published: e.target.value })} className="mt-1" />
      </div>
      <div>
        <Label>Jurisdiction (state / local / org)</Label>
        <Select value={form.jurisdiction_code || ""} onValueChange={(v) => setForm({ ...form, jurisdiction_code: v })}>
          <SelectTrigger data-testid={`${prefix}-jurisdiction-code`} className="mt-1"><SelectValue placeholder="Organizational (default)" /></SelectTrigger>
          <SelectContent className="max-h-72">{options.jurisdiction_codes.map((o) => <SelectItem key={o.code} value={o.code}>{o.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <Label>Document type</Label>
        <Select value={form.doc_type || ""} onValueChange={(v) => setForm({ ...form, doc_type: v })}>
          <SelectTrigger data-testid={`${prefix}-doc-type`} className="mt-1"><SelectValue placeholder="Select…" /></SelectTrigger>
          <SelectContent>{options.doc_types.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <Label>Status</Label>
        <Select value={form.status || "Active"} onValueChange={(v) => setForm({ ...form, status: v })}>
          <SelectTrigger data-testid={`${prefix}-status`} className="mt-1"><SelectValue /></SelectTrigger>
          <SelectContent>{(options.statuses.length ? options.statuses : ["Active"]).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <Label>Effective date · Version (optional)</Label>
        <div className="flex gap-2 mt-1">
          <Input data-testid={`${prefix}-effective`} type="date" value={form.effective_date || ""} onChange={(e) => setForm({ ...form, effective_date: e.target.value })} />
          <Input data-testid={`${prefix}-version-label`} placeholder="v1.0" value={form.version_label || ""} onChange={(e) => setForm({ ...form, version_label: e.target.value })} className="w-24" />
        </div>
      </div>
    </div>
  );
}

function MetaRow({ label, value, tid }) {
  return <div><dt className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{label}</dt><dd data-testid={tid} className={`text-xs mt-0.5 break-all ${value ? "text-slate-800" : "text-slate-400 italic"}`}>{value || UNAVAILABLE}</dd></div>;
}

const AUDIENCE_NAMES = { private: "Private/Nonprofit", federal: "Federal Government", state_local: "State/Local Government" };
const RETRIEVAL_TEXT = {
  link_only: "Official link only — source text is not currently searchable in LUMIS HR.",
  official_text: "Searchable — official source text stored in LUMIS HR.",
  official_excerpt: "Searchable — official source excerpt stored in LUMIS HR.",
  lumis_text: "Searchable — LUMIS HR-authored guidance text (not the official source text).",
  uploaded_text: "Searchable — uploaded document text.",
};
const REVIEW_MISSING = "Review date not yet recorded — verify against the current official source.";

export default function Documents() {
  const { user } = useAuth();
  const [docs, setDocs] = useState([]);
  const [tab, setTab] = useState("all");
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadForm, setUploadForm] = useState({ title: "", category: "Uploaded", citation: "", file: null });
  const [options, setOptions] = useState({ source_types: [], jurisdictions: [], statuses: [], doc_types: [], jurisdiction_codes: [] });
  const [jurFilter, setJurFilter] = useState("all");
  const [jurData, setJurData] = useState(null);
  const [urlOpen, setUrlOpen] = useState(false);
  const [urlForm, setUrlForm] = useState({ url: "", title: "", category: "Federal Policy", citation: "" });
  const [busy, setBusy] = useState(false);
  const [saveDialog, setSaveDialog] = useState(null);
  const [note, setNote] = useState("");

  const load = useCallback(() => api.get("/documents").then((r) => setDocs(r.data)), []);
  useEffect(() => { load(); api.get("/documents/meta/options").then((r) => setOptions(r.data)).catch(() => {}); api.get("/jurisdictions").then((r) => setJurData(r.data)).catch(() => {}); }, [load]);

  const isManager = user?.role === "manager";
  const onDocChanged = (d) => { setOpen(d); load(); };
  const openDoc = async (d) => { // list holds metadata only; full text comes from the secure detail endpoint
    setOpen({ ...d, content: null });
    try { const { data } = await api.get(`/documents/${d.id}`); setOpen((o) => (o && o.id === d.id ? data : o)); }
    catch { toast.error("Could not load this document"); }
  };
  const verify = async () => {
    try { const { data } = await api.post(`/documents/${open.id}/verify`, { note: "" }); toast.success("Marked as verified today"); onDocChanged(data); }
    catch { toast.error("Could not verify"); }
  };

  const CATS = [["all", "All"], ["Federal Policy", "Federal Policy"], ["State & Local", "State & Local"], ["Procedure", "Procedures"], ["Best Practices", "Best Practices"], ["LUMIS AI", "LUMIS AI"], ["uploaded", "Uploaded & Imported"]];
  const isStateLocal = (d) => d.jurisdiction_code && d.jurisdiction_code !== "US" && d.jurisdiction_code !== "ORG";
  // Relevance: sources for the user's employer type first, their selected state(s) ahead of other states, other-employer-type materials last (still browsable)
  const myStates = new Set(((jurData?.user_default?.length ? jurData.user_default : jurData?.org_default) || []).filter((c) => c !== "US" && c !== "ORG"));
  const rank = (d) => (d.applies_to_context === false ? 4 : 0) + (isStateLocal(d) ? (myStates.has(d.jurisdiction_code) ? -2 : 1) : 0) + (d.retrieval_status === "link_only" ? 1 : 0);
  const filtered = docs
    .filter((d) => tab === "all" || (tab === "uploaded" ? !d.is_seed : tab === "State & Local" ? isStateLocal(d) : d.category === tab))
    .filter((d) => jurFilter === "all" || d.jurisdiction_code === jurFilter)
    .filter((d) => !query || d.title.toLowerCase().includes(query.toLowerCase()) || d.category.toLowerCase().includes(query.toLowerCase()))
    .sort((a, b) => rank(a) - rank(b));

  const importUrl = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/documents/import-url", { ...urlForm, source_type: urlForm.source_type || null, jurisdiction: urlForm.jurisdiction || null, agency: urlForm.agency || null, published: urlForm.published || null, jurisdiction_code: urlForm.jurisdiction_code || null, doc_type: urlForm.doc_type || null, status: urlForm.status || "Active", effective_date: urlForm.effective_date || null, version_label: urlForm.version_label || null, audience: urlForm.audience || "all" });
      toast.success("Document imported and indexed");
      setUrlOpen(false);
      setUrlForm({ url: "", title: "", category: "Federal Policy", citation: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  const upload = async (e) => {
    e.preventDefault();
    if (!uploadForm.file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", uploadForm.title);
      fd.append("category", uploadForm.category);
      fd.append("citation", uploadForm.citation);
      ["source_type", "jurisdiction", "agency", "published", "jurisdiction_code", "doc_type", "effective_date", "version_label"].forEach((k) => fd.append(k, uploadForm[k] || ""));
      fd.append("status", uploadForm.status || "Active");
      fd.append("audience", uploadForm.audience || "all");
      fd.append("file", uploadForm.file);
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      setUploadOpen(false);
      setUploadForm({ title: "", category: "Uploaded", citation: "", file: null });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const confirmSave = async () => {
    try {
      await api.post("/highlights", { ...saveDialog, note });
      toast.success("Highlight saved");
      setSaveDialog(null); setNote("");
    } catch { toast.error("Could not save"); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#C7761A] font-bold">Library</div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#0A2A5E]">Documents & Policies</h1>
          <p className="text-sm text-slate-600 mt-1">Federal HR policies, procedures, and your uploaded SOPs — all sourced.</p>
        </div>
        {user?.role !== "manager" ? (
          <p data-testid="upload-permission-notice" className="text-xs text-slate-500 max-w-xs">Document uploads are available to authorized HR managers and organization administrators.</p>
        ) : (
          <div className="flex gap-2">
          <Dialog open={urlOpen} onOpenChange={setUrlOpen}>
            <DialogTrigger asChild>
              <Button data-testid="import-url-btn" variant="outline" className="border-[#F5A623] text-[#7C3F00] hover:bg-[#FDECD0]">
                <LinkIcon className="w-4 h-4 mr-2"/> Import from URL
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-display">Import a policy from the web</DialogTitle>
                <DialogDescription>Paste a DOL, EEOC, OPM, or OSHA link (PDF or web page). The text is pulled in and indexed for AI answers, with the URL kept as the source.</DialogDescription>
              </DialogHeader>
              <form onSubmit={importUrl} className="space-y-3">
                <div>
                  <Label>URL</Label>
                  <Input data-testid="import-url-input" required type="url" placeholder="https://www.dol.gov/..." value={urlForm.url} onChange={(e) => setUrlForm({...urlForm, url: e.target.value})}/>
                </div>
                <div>
                  <Label>Title</Label>
                  <Input data-testid="import-url-title" required value={urlForm.title} onChange={(e) => setUrlForm({...urlForm, title: e.target.value})}/>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Category</Label>
                    <Input data-testid="import-url-category" value={urlForm.category} onChange={(e) => setUrlForm({...urlForm, category: e.target.value})}/>
                  </div>
                  <div>
                    <Label>Citation</Label>
                    <Input data-testid="import-url-citation" placeholder="e.g. 29 CFR Part 825" value={urlForm.citation} onChange={(e) => setUrlForm({...urlForm, citation: e.target.value})}/>
                  </div>
                </div>
                <MetaSelects form={urlForm} setForm={setUrlForm} options={options} prefix="import-url" />
                <AudienceSelect form={urlForm} setForm={setUrlForm} prefix="import-url" />
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setUrlOpen(false)}>Cancel</Button>
                  <Button data-testid="import-url-submit" type="submit" disabled={busy} className="bg-[#0A2A5E] hover:bg-[#0F3F8A] text-white">
                    {busy ? "Importing..." : "Import"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
            <DialogTrigger asChild>
              <Button data-testid="upload-doc-btn" className="bg-[#0A2A5E] hover:bg-[#0F3F8A] text-white">
                <Upload className="w-4 h-4 mr-2"/> Upload document
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-display">Upload SOP or policy</DialogTitle>
                <DialogDescription>PDFs and text files. Content is indexed for AI answers.</DialogDescription>
              </DialogHeader>
              <form onSubmit={upload} className="space-y-3">
                <div>
                  <Label>Title</Label>
                  <Input data-testid="upload-title" required value={uploadForm.title} onChange={(e) => setUploadForm({...uploadForm, title: e.target.value})}/>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Category</Label>
                    <Input data-testid="upload-category" value={uploadForm.category} onChange={(e) => setUploadForm({...uploadForm, category: e.target.value})}/>
                  </div>
                  <div>
                    <Label>Citation</Label>
                    <Input data-testid="upload-citation" placeholder="e.g. Company SOP §4.2" value={uploadForm.citation} onChange={(e) => setUploadForm({...uploadForm, citation: e.target.value})}/>
                  </div>
                </div>
                <MetaSelects form={uploadForm} setForm={setUploadForm} options={options} prefix="upload" />
                <AudienceSelect form={uploadForm} setForm={setUploadForm} prefix="upload" />
                <div>
                  <Label>File (PDF or TXT)</Label>
                  <Input data-testid="upload-file" type="file" accept=".pdf,.txt" required onChange={(e) => setUploadForm({...uploadForm, file: e.target.files[0]})}/>
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setUploadOpen(false)}>Cancel</Button>
                  <Button data-testid="upload-submit" type="submit" disabled={busy} className="bg-[#0A2A5E] hover:bg-[#0F3F8A] text-white">
                    {busy ? "Uploading..." : "Upload"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="doc-tabs" className="flex-wrap h-auto">
            {CATS.map(([v, l]) => <TabsTrigger key={v} value={v} data-testid={`doc-tab-${v.replace(/\s+/g, "-").toLowerCase()}`}>{l}</TabsTrigger>)}
          </TabsList>
        </Tabs>
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
          <Input data-testid="doc-search" placeholder="Search title or category…" value={query} onChange={(e) => setQuery(e.target.value)} className="pl-9"/>
        </div>
        <Select value={jurFilter} onValueChange={setJurFilter}>
          <SelectTrigger data-testid="doc-jurisdiction-filter" className="w-56 h-10"><SelectValue placeholder="All jurisdictions" /></SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value="all">All jurisdictions</SelectItem>
            <SelectItem value="US">Federal</SelectItem>
            <SelectItem value="ORG">Organizational policy library</SelectItem>
            {(jurData?.states || []).map((s) => [
              <SelectItem key={s.code} value={s.code}>{s.name}{s.coverage === "none" ? " — no sources" : ""}</SelectItem>,
              ...s.locals.map((l) => <SelectItem key={l.code} value={l.code}>&nbsp;&nbsp;↳ {l.name}{l.coverage === "none" ? " — no sources" : ""}</SelectItem>),
            ])}
          </SelectContent>
        </Select>
      </div>
      {tab === "State & Local" && jurData && (
        <div data-testid="jurisdiction-coverage" className="mb-4 p-4 rounded-xl bg-white border border-slate-200">
          <div className="text-xs font-bold uppercase tracking-widest text-[#7C3F00]">Coverage by jurisdiction</div>
          <div className="flex flex-wrap gap-2 mt-2">
            {jurData.states.filter((s) => s.coverage !== "none" || s.locals.some((l) => l.coverage !== "none")).map((s) => (
              <button key={s.code} data-testid={`coverage-${s.code}`} onClick={() => setJurFilter(s.code)} className="flex items-center gap-2 px-2.5 py-1 rounded-lg border border-slate-200 hover:border-[#F5A623] text-xs">
                <span className="font-semibold text-[#0A2A5E]">{s.name}</span><CoverageBadge coverage={s.coverage} />
                {s.locals.map((l) => <span key={l.code} className="text-slate-500">· {l.name} <CoverageBadge coverage={l.coverage} /></span>)}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-slate-500 mt-3 leading-relaxed">{jurData.disclaimer} States not listed here have no verified LUMIS or organization-uploaded sources yet; appearing in the selector does not imply legal coverage.</p>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((d) => (
          <Card
            key={d.id}
            data-testid={`doc-card-${d.id}`}
            onClick={() => openDoc(d)}
            className="p-5 bg-white border-slate-200 hover-lift cursor-pointer"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#FDECD0] flex items-center justify-center flex-shrink-0">
                <FileText className="w-5 h-5 text-[#7C3F00]"/>
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-[#C7761A] font-bold">{d.category}</div>
                <h3 className="font-display font-bold text-[#0A2A5E] mt-1 line-clamp-2">{d.title}</h3>
                <p className="text-xs text-slate-500 mt-1 line-clamp-2">{d.summary}</p>
                <div className="mt-3 flex flex-wrap items-center gap-1.5">
                  <span className="inline-block text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-semibold">{d.citation}</span>
                  <SourceTypeBadge type={d.source_type} />
                  {isStateLocal(d) && <span data-testid={`doc-jur-${d.id}`} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-violet-100 text-violet-900"><MapPin className="w-3 h-3" /> {d.jurisdiction_name}</span>}
                  {d.status && d.status !== "Active" && <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-rose-100 text-rose-800">{d.status}</span>}
                  {/federal-sector/i.test(d.applicability || "") && <span data-testid={`doc-fedsector-${d.id}`} className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#FFF7E8] border border-[#F5A623] text-[#7C3F00]">Federal-sector applicability</span>}
                  {d.applies_to_context === false && <span data-testid={`doc-mismatch-${d.id}`} className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-200 text-slate-700">Different employer type: {(d.employer_audience || []).map((a) => AUDIENCE_NAMES[a] || a).join(", ")}</span>}
                  {d.retrieval_status === "link_only" && <span data-testid={`doc-linkonly-${d.id}`} className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border border-dashed border-slate-400 text-slate-600">Official link only</span>}
                  {(d.version || 1) > 1 && <span data-testid={`doc-version-${d.id}`} className="text-[10px] px-1.5 py-0.5 rounded bg-[#0A2A5E] text-white font-bold">v{d.version}</span>}
                  {d.source_url && (
                    <a href={d.source_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} data-testid={`doc-source-${d.id}`} className="text-[10px] text-[#0A2A5E] hover:underline inline-flex items-center gap-0.5">
                      Source <ExternalLink className="w-2.5 h-2.5"/>
                    </a>
                  )}
                </div>
              </div>
            </div>
          </Card>
        ))}
        {filtered.length === 0 && (
          <div className="col-span-full text-center py-12 text-slate-500">
            <BookOpen className="w-8 h-8 mx-auto mb-2 opacity-50"/>
            No documents match this filter.
          </div>
        )}
      </div>

      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-display">{open?.title}</DialogTitle>
            <DialogDescription className="flex items-center gap-2 flex-wrap">
              <span className="text-xs px-2 py-0.5 bg-[#FDECD0] text-[#7C3F00] rounded-full font-semibold">{open?.citation}</span>
              <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-700 rounded-full">{open?.category}</span>
              {open?.source_url && (
                <a href={open.source_url} target="_blank" rel="noreferrer" className="text-xs text-[#0A2A5E] hover:underline inline-flex items-center gap-1">
                  Official source <ExternalLink className="w-3 h-3"/>
                </a>
              )}
              {open?.file_id && (
                <button type="button" data-testid="doc-download-original" onClick={() => downloadFile(open.file_id, open.filename)} className="text-xs text-[#0A2A5E] hover:underline inline-flex items-center gap-1">
                  <FileText className="w-3 h-3"/> Download original ({open.filename})
                </button>
              )}
            </DialogDescription>
          </DialogHeader>
          {open && (
            <div data-testid="doc-source-details" className="rounded-lg border border-slate-200 bg-[#F5F9FE] p-3">
              <div className="flex flex-wrap items-center gap-2">
                <SourceTypeBadge type={open.source_type} testid="doc-source-type" />
                {open.jurisdiction && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border border-slate-300 text-slate-600">{open.jurisdiction}</span>}
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#0A2A5E] text-white">v{open.version || 1}</span>
                <span data-testid="doc-status" className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${open.status === "Active" || !open.status ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}`}>{open.status || "Active"}</span>
                {isManager && (
                  <div className="ml-auto flex gap-2">
                    <Button data-testid="doc-verify-btn" size="sm" variant="outline" onClick={verify} className="border-emerald-300 text-emerald-800 hover:bg-emerald-50"><BadgeCheck className="w-4 h-4 mr-1.5" /> Mark verified today</Button>
                    <DocumentVersions doc={open} onChanged={onDocChanged} />
                  </div>
                )}
              </div>
              {isManager && <DocumentAudience key={open.id} doc={open} onChanged={onDocChanged} />}
              <div className="mt-2"><span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block">Applicability</span><ApplicabilityNote text={open.applicability} testid="doc-meta-applicability" /></div>
              <p data-testid="doc-meta-retrieval" className={`mt-2 text-xs font-semibold ${open.retrieval_status === "link_only" ? "text-[#7C3F00]" : "text-slate-700"}`}>{RETRIEVAL_TEXT[open.retrieval_status] || "Retrieval status unknown — verify against the current official source."}</p>
              <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2 mt-2">
                <MetaRow label="Issuing organization" value={open.agency} tid="doc-meta-agency" />
                <MetaRow label="Official source link" value={open.source_url} tid="doc-meta-url" />
                <MetaRow label="Effective / revision date" value={open.effective_date || open.published} tid="doc-meta-effective" />
                <MetaRow label="Last reviewed by LUMIS HR" value={open.last_reviewed ? `${open.last_reviewed.slice(0, 10)}${open.retrieved_at ? " · official text retrieved" : ""}` : REVIEW_MISSING} tid="doc-meta-reviewed" />
                <MetaRow label="Section / regulation" value={open.section || open.citation} tid="doc-meta-section" />
                <MetaRow label="Date added to library" value={open.added_at ? open.added_at.slice(0, 10) : null} tid="doc-meta-added" />
                <MetaRow label="Verified by your organization" value={open.last_verified ? `${open.last_verified}${open.verified_by_name ? ` · ${open.verified_by_name}` : ""}` : null} tid="doc-meta-verified" />
              </dl>
            </div>
          )}
          <ScrollArea className="flex-1 mt-2">
            <HighlightableText
              testid="doc-content-view"
              onSave={(t) => setSaveDialog({
                text: t,
                source_type: "document",
                source_ref: open?.id,
                source_title: open?.title,
                citation: open?.citation,
              })}
            >
              {open?.content === null ? <div data-testid="doc-content-loading" className="text-sm text-slate-500 py-6 text-center">Loading document…</div> : <pre data-testid="doc-content" className="whitespace-pre-wrap text-sm leading-relaxed text-slate-800 font-sans">{open?.content}</pre>}
            </HighlightableText>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      <Dialog open={!!saveDialog} onOpenChange={(v) => !v && (setSaveDialog(null), setNote(""))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display">Save highlight</DialogTitle>
            <DialogDescription>From: <span className="font-semibold text-slate-800">{saveDialog?.source_title}</span></DialogDescription>
          </DialogHeader>
          <div className="p-3 rounded-lg bg-[#FFF7E8] border border-[#F5A623] text-sm text-slate-800 italic">"{saveDialog?.text}"</div>
          <Input data-testid="doc-highlight-note" placeholder="Add a personal note (optional)" value={note} onChange={(e) => setNote(e.target.value)}/>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => { setSaveDialog(null); setNote(""); }}>Cancel</Button>
            <Button data-testid="doc-highlight-save" onClick={confirmSave} className="bg-[#0A2A5E] hover:bg-[#0F3F8A] text-white">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
